import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import {
  ArrowLeft, Save, ChevronDown, ChevronUp,
  FileText, Package, MapPin, Calendar,
  Truck, Hash, Check, X,
  Building2, Send
} from 'lucide-react';
import { useDispatch, useSelector } from "react-redux";
import moment from 'moment';
import { getLoadPlanByID } from '@/redux/Load/actions';
import { SearchBar } from '../SearchBox/SearchBar';
import FileUpload from '../FileUpload/FileUpload';
import { updateLoadPlan } from '@/redux/Load/actions';
import { getAddressAPI } from '@/redux/Purchase/actions';
import { callLoader } from '@/redux/Loader/actions';
import { getItemColumns } from './columns';
import { DataTable } from '../ui/table';
import DatePicker from 'react-datepicker';
import { showToast } from '@/redux/Toast/actions';
import CelebrationButton from '../ui/CelebrationButton/CelebrationButton';
import PageHeader from '../FormHeader/formPageHeader';

/* ─── tiny helpers (mirrored from PurchaseOrderFormSleek) ─── */
const InputField = ({ label, required, children, className = '' }) => (
  <div className={className}>
    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

const SECTION_COLORS = {
  blue:   'bg-blue-500',
  teal:   'bg-teal-500',
  violet: 'bg-violet-500',
  cyan:   'bg-cyan-500',
  green:  'bg-emerald-500',
  indigo: 'bg-indigo-500',
  amber:  'bg-amber-500',
  orange: 'bg-orange-500',
  rose:   'bg-rose-500',
  dark:   'bg-slate-700',
};

/* ─── main component ─── */
const LoadPlanEditForm = ({ onBack, orderDataId = null }) => {

  const dispatch = useDispatch();
  const { countryList, incoTermsList, quantityTypeList, uomTypeList } = useSelector((state) => state.common);
  const [formData, setFormData] = useState();
  const [items, setItems] = useState([]);
  const { isAnimate } = useSelector((state) => state.toast);

  /* section expand state — all open by default */
  const [expandedSections, setExpandedSections] = useState({
    orderDetails: true,
    tradePartner: true,
    routing:      true,
    commodity:    true,
  });

  /* scroll tracking */
  const [currentStep, setCurrentStep] = useState(0);
  const orderDetailsRef = useRef(null);
  const tradePartnerRef = useRef(null);
  const routingRef      = useRef(null);
  const commodityRef    = useRef(null);

  const sectionRefs = {
    orderDetails: orderDetailsRef,
    tradePartner: tradePartnerRef,
    routing:      routingRef,
    commodity:    commodityRef,
  };

  const steps        = ['Order Details', 'Trade Partner', 'Routing', 'Commodity'];
  const stepSectionMap = useMemo(() => ['orderDetails', 'tradePartner', 'routing', 'commodity'], []);

  /* intersection observer for step highlighting */
  useEffect(() => {
    const observers = [];
    stepSectionMap.forEach((key, index) => {
      const ref = sectionRefs[key];
      if (!ref?.current) return;
      const observer = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setCurrentStep(index); },
        { root: null, rootMargin: '-30% 0px -60% 0px', threshold: 0 }
      );
      observer.observe(ref.current);
      observers.push(observer);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, [formData, stepSectionMap]);

  const scrollToSection = (index) => {
    const key = stepSectionMap[index];
    const ref = sectionRefs[key];
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setExpandedSections((p) => ({ ...p, [key]: true }));
    }
  };

  const toggleSection = (section) =>
    setExpandedSections((p) => ({ ...p, [section]: !p[section] }));

  /* ─── ALL ORIGINAL LOGIC PRESERVED ─── */

  useEffect(() => {
    if (orderDataId != 0 && orderDataId != null) {
      _getLoadPlan();
    } else {
      _setNewLoadPlan();
    }
  }, [orderDataId]);

  const _getLoadPlan = () => {
    dispatch(callLoader(1));
    getLoadPlanByID(orderDataId).then(res => {
      dispatch(callLoader(0));
      setFormData(res);
      setItems(res?.lineItems ?? []);
    }).catch(err => {
      dispatch(callLoader(0));
    });
  };

  const _setNewLoadPlan = async () => {
    setFormData(null);
    setItems([]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    if (field === 'quantity' || field === 'price') {
      const qty = parseFloat(updated[index].quantity) || 0;
      const price = parseFloat(updated[index].price) || 0;
      updated[index].total = (qty * price).toFixed(2);
    }
    console.log(updated);
    setItems(updated);
  };

  const formhandleSubmit = async (e) => {
    e.preventDefault();
  };

  const _setAddress = async (type, id) => {
    const address = await getAddressAPI(id);
    setFormData(prev => ({ ...prev, [`${type}`]: address }));
  };

  const handleSubmit = async (e) => {
    console.log('Form Data:', formData, 'Items:', items);
    try {
      var itmPayload = [];
      items.map((v, i) => {
        const ob = {
          "LOADPLAN_LINEITEM_ID": v?.loadplanLineitemId ?? 0,
          "SKU": v?.sku,
          "SKU_ITEM_DESC": v?.skuItemDesc,
          "UOT": v?.uot,
          "CASES_PER_UOT": v?.casesPerUot,
          "GROSS_WEIGHT_KGS": v?.grossWeightKgs,
          "NET_WEIGHT_KGS": v?.netWeightKgs,
          "PACKING": v?.packing,
          "QUANTITY": v?.quantity,
          "METRIC_ID": v?.metricId,
          "METRIC_TYPE": v?.metricType,
          "PRICE": v?.price,
          "SO_NUMBER": v?.soNumber,
          "PO_NUMBER": v?.poNumber,
          "IS_ACTIVE": v?.isActive != false ? true : false
        };
        v?.sku ? itmPayload.push(ob) : null;
      });
      const payload = {
        "LO_ID": formData?.loId ?? 0,
        "COMPANY_ID": 1,
        "LO_DATE": formData?.loDate ? moment(formData?.loDate).format('YYYY-MM-DD') : null,
        "LO_SHIP_BY_DATE": formData?.loShipByDate ? moment(formData?.loShipByDate).format('YYYY-MM-DD') : null,
        "CUSTOMER_ID": formData?.customerId,
        "CUSTOMER": formData?.customer,
        "CUSTOMER_ADDRESS": formData?.customerAddress,
        "CONSIGNEE_ID": formData?.consigneeId,
        "CONSIGNEE": formData?.consignee,
        "CONSIGNEE_ADDRESS": formData?.consigneeAddress,
        "INCOTERM_ID": formData?.incotermId,
        "INCOTERM": formData?.incoterm,
        "DESTINATION_COUNTRY": formData?.destinationCountry,
        "WAREHOUSE_ID": formData?.warehouseId,
        "WAREHOUSE": formData?.warehouse,
        "WAREHOUSE_ADDRESS": formData?.warehouseAddress,
        "WAREHOUSE_REMARK": formData?.warehouseRemark,
        "INTERNAL_REMARKS": formData?.internalRemarks,
        "MODIFIED_BY": "Admin",
        "IS_ACTIVE": formData?.isActive != false ? true : false,
        "LineItems": itmPayload
      };

      if (orderDataId != 0 && orderDataId != null) {
        dispatch(callLoader(1));
        const updateDetails = await updateLoadPlan(payload);
        dispatch(callLoader(0));
        if (updateDetails?.message) {
          dispatch(showToast("Load Plan saved successfully", "success", true));
        } else {
          throw new Error('Load Plan Not Updated!');
        }
      } else {
        throw new Error('Load Plan Not Updated!');
      }
    } catch (error) {
      dispatch(showToast(error?.message || error, "error"));
      dispatch(callLoader(0));
    }
  };

  /* isSectionFilled */
  const isSectionFilled = useCallback((key) => {
    if (!formData) return false;
    switch (key) {
      case 'orderDetails': return !!(formData?.loNumber || formData?.incotermId || formData?.loDate);
      case 'tradePartner': return !!(formData?.customer || formData?.consignee || formData?.warehouse);
      case 'routing':      return !!(formData?.destinationCountry || formData?.loShipByDate);
      case 'commodity':    return !!(items?.length > 0 && items.some(i => i.sku));
      default: return false;
    }
  }, [formData, items]);

  /* css shared */
  const inputClass = 'w-full px-3 py-[0.6rem] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all';
  const selectClass = 'w-full px-3 py-[0.5rem] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all appearance-none cursor-pointer';

  const columns = getItemColumns({
    items,
    handleItemChange,
    uomTypeList,
    quantityTypeList,
  });

  /* ─── Section Header — exact copy of PO style ─── */
  const SectionHeader = ({ icon: Icon, title, color = 'blue', section, badge, action }) => {
    const iconBg = SECTION_COLORS[color] ?? color ?? SECTION_COLORS.blue;
    return (
      <button
        onClick={() => toggleSection(section)}
        className="w-full flex items-center justify-between p-4 border-b bg-gradient-to-r to-slate-100 dark:from-slate-800 dark:to-slate-700 cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 mx-2 rounded-lg shadow-md ${iconBg}`}>
            <Icon className="w-4 h-4 text-white" />
          </div>
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-200">{title}</h2>
          {badge && (
            <span className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 text-xs font-medium rounded-full">
              {badge}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {action}
          <div className={`p-1.5 rounded-md transition-colors ${expandedSections[section] ? 'bg-indigo-100 dark:bg-indigo-900/30' : 'bg-slate-200 dark:bg-slate-700'}`}>
            {expandedSections[section]
              ? <ChevronUp className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              : <ChevronDown className="w-4 h-4 text-slate-500 dark:text-slate-400" />}
          </div>
        </div>
      </button>
    );
  };

  /* ══════════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════════ */
  return (
    <div className="h-full bg-slate-50 dark:bg-slate-950">

      {/* ── STICKY PAGE HEADER ── */}
      <div className="page-header-sticky sticky top-0 z-50">
        <PageHeader
          icon={<FileText className="w-5 h-5 text-white" />}
          title="Load Plan"
          badge={`LP No. ${formData?.loNumber ?? '—'}`}
          subtitle={
            <>{'Editing '}<span className="font-semibold text-slate-700 dark:text-slate-300">{formData?.loNumber ?? orderDataId}</span></>
          }
          actions={[
            { label: 'Back', icon: <ArrowLeft className="w-4 h-4" />, onClick: onBack, variant: 'ghost' },
          ]}
          onSubmit={handleSubmit}
          submitLabel="Submit Plan"
          isSuccess={isAnimate}
        />
        <div style={{ height: 'var(--page-header-height)' }} />
      </div>

      <div className="p-4 space-y-3 pb-16">

        {/* ── PROGRESS STEPS (exact PO style) ── */}
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 px-4 py-3 overflow-x-auto">
          <div className="flex items-center w-full justify-between gap-0">
            {steps.map((step, index) => {
              const key    = stepSectionMap[index];
              const filled = isSectionFilled(key);
              const done   = filled && index !== currentStep;
              const active = index === currentStep;
              return (
                <React.Fragment key={step}>
                  <div
                    className={[
                      'flex items-center gap-2 h-8 pl-2 pr-3 rounded-full text-xs font-medium cursor-pointer transition-all duration-200 select-none',
                      done   && 'text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20',
                      active && 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
                      !done && !active && 'text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800',
                    ].filter(Boolean).join(' ')}
                    onClick={() => scrollToSection(index)}
                  >
                    <span className={[
                      'w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold flex-shrink-0',
                      done   && 'bg-green-500 text-white',
                      active && 'bg-blue-600 text-white',
                      !done && !active && 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400',
                    ].filter(Boolean).join(' ')}>
                      {done ? <Check className="w-3 h-3" /> : index + 1}
                    </span>
                    <span className="hidden sm:inline">{step}</span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={[
                      'flex-1 h-0.5 min-w-[8px] mx-1 rounded-full transition-colors duration-300',
                      done ? 'bg-green-400 dark:bg-green-600' : 'bg-slate-200 dark:bg-slate-700',
                    ].join(' ')} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <form id="lp-form" onSubmit={formhandleSubmit} className="space-y-3">

          {/* ── ORDER DETAILS ── */}
          <div
            ref={orderDetailsRef}
            className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200"
          >
            <SectionHeader icon={Hash} title="Order Details" color="blue" section="orderDetails" />
            {expandedSections.orderDetails && (
              <div className="p-4">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <InputField label="Load Plan #" required>
                    <input
                      type="text"
                      name="loNumber"
                      value={formData?.loNumber ?? ''}
                      readOnly
                      className={inputClass + ' bg-slate-50 dark:bg-slate-900 cursor-not-allowed'}
                    />
                  </InputField>

                  <InputField label="LP SO #" required>
                    <input
                      type="text"
                      name="loSoNumber"
                      value={formData?.loSoNumber ?? ''}
                      readOnly
                      className={inputClass + ' bg-slate-50 dark:bg-slate-900 cursor-not-allowed'}
                    />
                  </InputField>

                  <InputField label="Inco Terms">
                    <select
                      name="incotermId"
                      value={formData?.incotermId ?? ''}
                      onChange={(d) => {
                        handleInputChange({ target: { name: 'incotermId', value: d.target.value } });
                        handleInputChange({ target: { name: 'incoterm', value: d.target.options[d.target.selectedIndex].text } });
                      }}
                      className={selectClass}
                    >
                      <option value="">SELECT TERMS</option>
                      {incoTermsList?.map((term, key) => (
                        <option key={key} value={term?.id}>{term?.incoterm}</option>
                      ))}
                    </select>
                  </InputField>

                  <InputField label="Date">
                    <DatePicker
                      selected={formData?.loDate ? new Date(formData.loDate) : null}
                      showIcon
                      onChange={(date) =>
                        handleInputChange({
                          target: { name: 'loDate', value: moment(date).format('YYYY-MM-DD') },
                        })
                      }
                      dateFormat="dd MMM yyyy"
                      placeholderText="Select Date"
                      className={inputClass}
                    />
                  </InputField>
                </div>
              </div>
            )}
          </div>

          {/* ── TRADE PARTNER + ROUTING side by side ── */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">

            {/* Trade Partner */}
            <div
              ref={tradePartnerRef}
              className="xl:col-span-2 bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200"
            >
              <SectionHeader icon={Building2} title="Trade Partner" color="cyan" section="tradePartner" />
              {expandedSections.tradePartner && (
                <div className="p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">

                    {/* Customer */}
                    <div>
                      <InputField label="Customer" required>
                        <SearchBar
                          searchAPI={'v1/AutoSuggestion/GetVendorSuggestion?Prefix='}
                          selectReacord={(d) => {
                            handleInputChange({ target: { name: 'customer', value: d?.[1] } });
                            handleInputChange({ target: { name: 'customerId', value: d?.[0] } });
                            _setAddress('customerAddress', d?.[0]);
                          }}
                          value={formData?.customer}
                        />
                      </InputField>
                      <textarea
                        name="customerAddress"
                        value={formData?.customerAddress ?? ''}
                        onChange={handleInputChange}
                        rows="5"
                        placeholder="Customer address..."
                        className={inputClass + ' mt-2 resize-none'}
                      />
                    </div>

                    {/* Consignee */}
                    <div>
                      <InputField label="Consignee">
                        <SearchBar
                          searchAPI={'v1/AutoSuggestion/GetConsigneeSuggestion?Prefix='}
                          selectReacord={(d) => {
                            handleInputChange({ target: { name: 'consignee', value: d?.[1] } });
                            handleInputChange({ target: { name: 'consigneeId', value: d?.[0] } });
                            _setAddress('consigneeAddress', d?.[0]);
                          }}
                          value={formData?.consignee}
                        />
                      </InputField>
                      <textarea
                        name="consigneeAddress"
                        value={formData?.consigneeAddress ?? ''}
                        onChange={handleInputChange}
                        rows="5"
                        placeholder="Consignee address..."
                        className={inputClass + ' mt-2 resize-none'}
                      />
                    </div>

                    {/* Warehouse */}
                    <div>
                      <InputField label="Warehouse">
                        <SearchBar
                          searchAPI={'v1/AutoSuggestion/GetWarehouseSuggestion?Prefix='}
                          value={formData?.warehouse}
                          selectReacord={(d) => {
                            handleInputChange({ target: { name: 'warehouse', value: d?.[1] } });
                            handleInputChange({ target: { name: 'warehouseId', value: d?.[0] } });
                            _setAddress('warehouseAddress', d?.[0]);
                          }}
                        />
                      </InputField>
                      <textarea
                        name="warehouseAddress"
                        value={formData?.warehouseAddress ?? ''}
                        onChange={handleInputChange}
                        rows="5"
                        placeholder="Warehouse address..."
                        className={inputClass + ' mt-2 resize-none'}
                      />
                    </div>

                    {/* Remarks — spans 2 cols */}
                    <div>
                      <InputField label="Remarks">
                        <textarea
                          name="internalRemarks"
                          value={formData?.internalRemarks ?? ''}
                          onChange={handleInputChange}
                          rows="7"
                          placeholder="Enter remarks..."
                          className={inputClass + ' resize-none'}
                        />
                      </InputField>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Routing */}
            <div
              ref={routingRef}
              className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200"
            >
              <SectionHeader icon={Truck} title="Routing" color="orange" section="routing" />
              {expandedSections.routing && (
                <div className="p-4 space-y-4">
                  <InputField label="Delivered By Date">
                    <DatePicker
                      selected={formData?.loShipByDate ? new Date(formData.loShipByDate) : null}
                      showIcon
                      onChange={(date) =>
                        handleInputChange({
                          target: { name: 'loShipByDate', value: moment(date).format('YYYY-MM-DD') },
                        })
                      }
                      dateFormat="dd MMM yyyy"
                      placeholderText="Select Date"
                      className={inputClass}
                    />
                  </InputField>

                  <InputField label="Dest. Country">
                    <select
                      name="destinationCountry"
                      value={formData?.destinationCountry ?? ''}
                      onChange={handleInputChange}
                      className={selectClass}
                    >
                      <option value="">SELECT COUNTRY OF DESTINATION</option>
                      {countryList?.map((country, key) => (
                        <option key={key} value={country.incoterm}>{country.incoterm}</option>
                      ))}
                    </select>
                  </InputField>
                </div>
              )}
            </div>
          </div>

          {/* ── COMMODITY TABLE ── */}
          <div
            ref={commodityRef}
            className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200"
          >
            <SectionHeader
              icon={Package}
              title="Commodity"
              color="green"
              section="commodity"
              badge={`${items.filter(i => i?.isActive !== false).length} items`}
            />
            {expandedSections.commodity && (
              <DataTable
                data={items.filter((i) => i?.isActive !== false)}
                columns={columns}
                tdClass="px-2 py-2 text-sm"
                thClass="px-2 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider"
                trClass=" hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer"
                htrClass="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50"
                boxClass="overflow-x-auto overflow-y-visible pb-[20vh] text-xs"
              />
            )}
          </div>

          {/* File Upload */}
          {orderDataId != 0 && orderDataId != null && (
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden p-4">
              <FileUpload
                orderId={orderDataId}
                orderNum={formData?.loNumber}
                type={"loadplan"}
              />
            </div>
          )}
        </form>

        {/* ── BOTTOM ACTION BAR (exact PO style) ── */}
        <div className="p-3 bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-500 dark:text-slate-400">
              <span className="text-red-500">*</span> Required fields must be filled
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-4 h-[40px] rounded-[9px] transition-all duration-150 text-slate-600 hover:bg-red-50 dark:text-slate-400 dark:hover:bg-slate-800"
              >
                <X className="w-4 h-4" />
                <span className="hidden sm:inline">Cancel</span>
              </button>

              <div className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-1" />

              <CelebrationButton
                onClick={() => handleSubmit()}
                label="Submit Plan"
                className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-4 h-[40px] rounded-[9px] transition-all duration-150 bg-green-600 text-white hover:bg-green-700"
                icon={<Send className="w-4 h-4" />}
                isSuccess={isAnimate}
              />
            </div>
          </div>
        </div>

      </div>{/* /p-4 */}
    </div>
  );
};

export default LoadPlanEditForm;